import { useAppSelector } from '@app/store';
import { useEffect, useMemo, useRef } from 'react';
import { useParams } from 'react-router-dom';

import { selectContextMenuTabId } from '../../../selector';
import { ContentType, contentTypeToEditorTab } from '../domain';
import { selectContentComposerContentHistory } from '../selector';

const STORAGE_KEY_PREFIX = 'content-composer-last-viewed';

function getLastViewedContentId(
  recordingId: string,
  contentType: ContentType,
): string | null {
  try {
    const key = `${STORAGE_KEY_PREFIX}-${recordingId}`;
    const stored = localStorage.getItem(key);
    if (!stored) return null;

    const parsed = JSON.parse(stored) as Record<string, string | null>;
    return parsed[contentType] ?? null;
  } catch {
    return null;
  }
}

function setLastViewedContentId(
  recordingId: string,
  contentType: ContentType,
  contentId: string | null,
): void {
  try {
    const key = `${STORAGE_KEY_PREFIX}-${recordingId}`;
    const stored = localStorage.getItem(key);
    const current = stored ? JSON.parse(stored) : {};

    current[contentType] = contentId;
    localStorage.setItem(key, JSON.stringify(current));
  } catch {
    // Silently fail if localStorage is not available
  }
}

/**
 * Hook to manage unseen content notification badges.
 * Compares latest content ID from Redux with "last viewed" ID in localStorage.
 * Auto-marks as seen when user views the tab (only for FINISHED/FAILED content).
 */
export function useUnseenContent(contentType: ContentType): boolean {
  const { recordingId } = useParams<{ recordingId: string }>();
  const selectedTabId = useAppSelector(selectContextMenuTabId);
  const contentHistory = useAppSelector(selectContentComposerContentHistory);
  const history = contentHistory[contentType];

  const latestContentItem = useMemo(() => {
    if (history?.history && history.history.length > 0) {
      return history.history[0];
    }
    return null;
  }, [history]);

  const latestContentId = latestContentItem?.id ?? null;
  const latestContentStatus = latestContentItem?.status ?? null;
  const isContentViewable =
    latestContentStatus === 'FINISHED' || latestContentStatus === 'FAILED';
  const isTabSelected = selectedTabId === contentTypeToEditorTab[contentType];

  const wasTabSelectedRef = useRef(false);
  const savedContentIdRef = useRef<string | null>(null);

  useEffect(() => {
    if (!recordingId || !latestContentId || !isContentViewable) {
      wasTabSelectedRef.current = isTabSelected;
      return;
    }

    if (isTabSelected) {
      const justBecameSelected = !wasTabSelectedRef.current;
      const contentChangedWhileViewing =
        wasTabSelectedRef.current &&
        latestContentId !== savedContentIdRef.current;

      if (justBecameSelected || contentChangedWhileViewing) {
        setLastViewedContentId(recordingId, contentType, latestContentId);
        savedContentIdRef.current = latestContentId;
      }
    }

    wasTabSelectedRef.current = isTabSelected;
  }, [
    isTabSelected,
    recordingId,
    contentType,
    latestContentId,
    isContentViewable,
  ]);

  const hasUnseen = useMemo(() => {
    if (!recordingId || isTabSelected || !isContentViewable) {
      return false;
    }

    const lastViewedContentId = getLastViewedContentId(
      recordingId,
      contentType,
    );
    return !!latestContentId && latestContentId !== lastViewedContentId;
  }, [
    recordingId,
    contentType,
    latestContentId,
    isTabSelected,
    isContentViewable,
  ]);

  return hasUnseen;
}
